import application from "../core/application";
import {FormEvent, useEffect, useState} from "react";
import authenticationService, {AUTHENTICATION_ERROR} from "./authentication.service";
import {AppError} from "../common/app-error";
import {useNavigate} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import {analyticsService} from "../core/analytics";

type FormError = {
  error: string | null;
  email: string | null;
  password: string | null;
};

const emptyError: FormError = {error: null, email: null, password: null};

export const LoginPage = () => {
  const [error, setError] = useState<FormError>(emptyError);
  const [loading, setLoading] = useState<boolean>(false);
  const [user] = application.user.useState();
  const navigate = useNavigate();
  const business = application.bussiness.get();
  const brandUrl = business?.image;

  useEffect(() => {
    if (user) return navigate("/painel");
  }, [user, navigate]);

  const login = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(emptyError);
    setLoading(true);
    const formData = new FormData(event.currentTarget);

    const email = formData.get("email")?.toString();
    const password = formData.get("password")?.toString();

    if (!email || !password) {
      setError({
        ...emptyError,
        email: !email ? "Informe seu email" : null,
        password: !password ? "Informe sua senha" : null,
      });
      setLoading(false);
      return;
    }

    try {
      analyticsService.trackEvent("login_attempt", {
        has_email: Boolean(email?.trim()),
      });
      const success = await authenticationService.login(email, password);
      if (!success) {
        analyticsService.trackEvent("login_failed", {reason: "unknown"});
        setError((e) => ({
          ...e,
          error: "Não foi possível efetuar o login, erro desconhecido.",
        }));
        return;
      }

      analyticsService.trackEvent("login_success");
      navigate("/painel");
    } catch (err) {
      if ((err as AppError).key === AUTHENTICATION_ERROR.WRONG_USERNAME) {
        analyticsService.trackEvent("login_failed", {reason: "wrong_username"});
        setError({
          error:
            "Não foi possível efetuar o login, os dados informados estão incorretos.",
          email: "Email informado é inválido.",
          password: null,
        });
        return;
      }

      if ((err as AppError).key === AUTHENTICATION_ERROR.WRONG_PASSWORD) {
        analyticsService.trackEvent("login_failed", {reason: "wrong_password"});
        setError({
          error:
            "Não foi possível efetuar o login, os dados informados estão incorretos.",
          password: "Senha informada é inválida.",
          email: null,
        });
        return;
      }

      analyticsService.trackEvent("login_failed", {reason: "unknown_exception"});
      setError((e) => ({
        ...e,
        error:
          "Não foi possível efetuar o login, ocorreu um erro desconhecido.",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: {xs: "column", md: "row"},
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: {xs: 220, md: "auto"},
          backgroundColor: "#0f172a",
          backgroundImage: brandUrl ? `url(${brandUrl})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.92) 0%, rgba(13,148,136,0.45) 100%)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            minHeight: {xs: 220, md: "100%"},
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            p: {xs: 3, md: 5},
            color: "common.white",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.03em",
              mb: 1,
              maxWidth: 480,
            }}
          >
            {business?.name ?? "Bem-vindo"}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.88,
              maxWidth: 420,
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            Acesse sua conta para continuar. Ambiente seguro e pensado para o
            dia a dia do seu negócio.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: {xs: 2, sm: 4},
          bgcolor: "background.default",
        }}
      >
        <Paper
          sx={{
            p: {xs: 3, sm: 4},
            width: "100%",
            maxWidth: 420,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              "0 1px 2px rgba(15, 23, 42, 0.04), 0 24px 48px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/")}
            size="small"
            sx={{ mb: 2, alignSelf: "flex-start", color: "text.secondary", px: 0, minWidth: 0, "&:hover": { bgcolor: "transparent", color: "text.primary" } }}
          >
            Voltar
          </Button>

          <Stack spacing={0.5} sx={{mb: 3}}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LockOutlinedIcon color="primary" sx={{fontSize: 28}} />
              <Typography variant="h5" component="h1">
                Entrar
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Informe seu email e senha para acessar o painel.
            </Typography>
          </Stack>

          <Box component="form" onSubmit={login} noValidate>
            <Stack spacing={2.5}>
              {error.error ? (
                <Alert severity="error" variant="outlined">
                  {error.error}
                </Alert>
              ) : null}

              <TextField
                name="email"
                label="Email"
                placeholder="voce@empresa.com"
                fullWidth
                required
                autoComplete="email"
                error={Boolean(error.email)}
                helperText={error.email ?? undefined}
              />

              <TextField
                name="password"
                label="Senha"
                placeholder="••••••••"
                type="password"
                fullWidth
                required
                autoComplete="current-password"
                error={Boolean(error.password)}
                helperText={error.password ?? undefined}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? "Entrando…" : "Entrar"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
